import getConfig from 'next/config';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { boatMakeAndModel } from '../../../helpers';
import { centsToDollars, dollarsNoCentsWithSymbol } from '../../../helpers/currency';
import { RENTER_APP_ID, getItunesAppUniversalLinkMetaTag } from '../../../helpers/templateHelpers';
import SEO from '../../SEO';
import { useBoatDetails, useInfiniteBoatReviews } from './hooks';

const { BS_ENV, RAILS_URL } = getConfig().publicRuntimeConfig;

const MetaInfo = ({ boatId }: { boatId: string }) => {
  const router = useRouter();
  const { boatDetails } = useBoatDetails(boatId);
  const { pages: reviews } = useInfiniteBoatReviews(boatId);

  const filteredBoatMakeAndModel = boatMakeAndModel(boatDetails).replace(/Other/gi, 'Boat');
  const boatMake = boatDetails?.make;
  const ownerName = boatDetails?.primary_manager?.first_name;
  const boatLocation = `${boatDetails?.location?.city}, ${boatDetails?.location?.state}`;

  const trimmedDescription = (descLength) =>
    boatDetails.listing_description?.length > descLength
      ? `${boatDetails.listing_description?.substring(0, descLength)}...`
      : boatDetails.listing_description;

  const active = boatDetails.activated;

  const preview = router.asPath.includes('preview/');
  const canonicalTag = `${RAILS_URL}/boats/${boatId}`;

  const title = `Rent a ${filteredBoatMakeAndModel} in ${boatLocation} on Boatsetter`;
  const description = `Rent for ${dollarsNoCentsWithSymbol(
    boatDetails?.cheapest_package?.price ?? boatDetails.primary_rate
  )}. ${trimmedDescription(130)}`;

  const flatReviews = reviews.map(({ data }) => data).flat();

  return (
    <>
      <SEO
        title={title}
        description={description}
        ogDescription={description}
        ogImage={boatDetails?.best_photo?.standard?.url}
        noIndex={boatDetails?.state === 'pending'}
      />
      <Head>
        {(BS_ENV === 'staging' || BS_ENV === 'production') && active && !preview && (
          <link rel="canonical" href={canonicalTag} key="canonical" />
        )}
        {getItunesAppUniversalLinkMetaTag(router.asPath, RENTER_APP_ID)}
        {(preview || !active) && <meta name="robots" content="noindex" />}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Home',
                  item: 'https://www.boatsetter.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'Boat Rentals',
                  item: 'https://www.boatsetter.com/boat-rentals',
                },
                ...(boatDetails?.location?.city
                  ? [
                      {
                        '@type': 'ListItem',
                        position: 3,
                        name: `${boatDetails.location.city}, ${boatDetails.location.state} Boat Rentals`,
                        item: `https://www.boatsetter.com/boat-rentals/${boatDetails.location.city
                          .toLowerCase()
                          .replace(/\s+/g, '-')}--${boatDetails.location.state?.toLowerCase()}--united-states`,
                      },
                      {
                        '@type': 'ListItem',
                        position: 4,
                        name: boatDetails.listing_tagline,
                      },
                    ]
                  : [
                      {
                        '@type': 'ListItem',
                        position: 3,
                        name: boatDetails.listing_tagline,
                      },
                    ]),
              ],
            }),
          }}
        />
      </Head>

      <div itemType="https://schema.org/Product" itemScope>
        <meta itemProp="name" content={boatDetails.listing_tagline} />
        {boatDetails?.best_photo?.standard && <link itemProp="image" href={boatDetails?.best_photo?.standard?.url} />}
        {(boatDetails?.photos || []).map((photo) => (
          <link key={photo.standard.url} itemProp="image" href={photo.standard.url} />
        ))}
        <meta itemProp="description" content={boatDetails.listing_description} />
        {boatMake && (
          <div itemProp="brand" itemType="https://schema.org/Brand" itemScope>
            <meta itemProp="name" content={boatMake} />
          </div>
        )}
        {!!boatDetails.review_count && !!boatDetails.rating && (
          <div itemProp="aggregateRating" itemType="https://schema.org/AggregateRating" itemScope>
            <meta itemProp="reviewCount" content={String(boatDetails.review_count)} />
            <meta itemProp="ratingValue" content={String(boatDetails.rating)} />
            <meta itemProp="bestRating" content="5" />
            <meta itemProp="worstRating" content="1" />
          </div>
        )}
        <div itemProp="offers" itemType="https://schema.org/Offer" itemScope>
          <link itemProp="url" href={canonicalTag} />
          <meta itemProp="availability" content="https://schema.org/InStock" />
          <meta itemProp="priceCurrency" content="USD" />
          <meta
            itemProp="price"
            content={`${centsToDollars(boatDetails?.cheapest_package?.price ?? boatDetails.primary_rate)}`}
          />
          <meta itemProp="priceValidUntil" content={`${new Date().getFullYear()}-12-31`} />
          {ownerName && (
            <div itemProp="seller" itemType="https://schema.org/Person" itemScope>
              <meta itemProp="name" content={ownerName} />
            </div>
          )}
        </div>
        {!!boatDetails.review_count &&
          flatReviews?.length > 0 &&
          flatReviews?.map((review) => (
            <div key={review.id} itemProp="review" itemType="https://schema.org/Review" itemScope>
              <meta itemProp="datePublished" content={review?.created_at} />
              <meta itemProp="reviewBody" content={review?.text} />
              <div itemProp="author" itemType="https://schema.org/Person" itemScope>
                <meta itemProp="name" content={`${review?.author?.first_name} ${review?.author?.last_name}`} />
              </div>
              <div itemProp="reviewRating" itemType="https://schema.org/Rating" itemScope>
                <meta itemProp="ratingValue" content={String(review?.rating)} />
                <meta itemProp="bestRating" content="5" />
                <meta itemProp="worstRating" content="1" />
              </div>
            </div>
          ))}
      </div>
    </>
  );
};

export default MetaInfo;
